// src/pages/Reports/IssuanceReporting/ReconciliationDiscrepancy/ConfirmReconciliationModal.tsx
// RPT-009 (README.md mục 7) — xác nhận đối soát 1 đại lý theo 1 kỳ phát hành. BE tự tính lại
// Deliverd/Recalled/Sold từ nguồn (không tin số FE gửi lên) — FE chỉ cần nhập số chênh lệch phát
// hiện qua đối chiếu thực tế với đại lý, kèm lý do bắt buộc nếu khác 0 (ConfirmAgentReconciliationCommand).

import React, { useEffect, useState } from 'react';
import { Button, Form, FormGroup, Input, Label, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { toast } from 'react-toastify';

import { confirmAgentReconciliationApi } from 'helpers/fakebackend_helper';
import { AgentMap, getAgentName } from 'helpers/agentLookup';
import NumberInput from 'Components/Common/NumberInput';
import { AgentReconciliation } from '../shared/types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('reports_issuance_reporting');

interface ConfirmReconciliationModalProps {
  isOpen: boolean;
  planId: number;
  target: AgentReconciliation | null;
  agentMap: AgentMap;
  onClose: () => void;
  onConfirmed: () => void;
}

const ConfirmReconciliationModal: React.FC<ConfirmReconciliationModalProps> = ({
  isOpen,
  planId,
  target,
  agentMap,
  onClose,
  onConfirmed,
}) => {
  const [discrepancyQuantity, setDiscrepancyQuantity] = useState(0);
  const [discrepancyReason, setDiscrepancyReason] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && target) {
      setDiscrepancyQuantity(target.discrepancyQuantity || 0);
      setDiscrepancyReason(target.discrepancyReason || '');
      setNote(target.note || '');
    }
  }, [isOpen, target]);

  const handleToggle = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!target) return;
    if (discrepancyQuantity !== 0 && !discrepancyReason.trim()) {
      toast.error(st('bat_buoc_nhap_ly_do_khi_message'));
      return;
    }
    setSubmitting(true);
    try {
      await confirmAgentReconciliationApi({
        issuancePlanId: planId,
        agentId: target.agentId,
        discrepancyQuantity,
        discrepancyReason: discrepancyReason.trim() || undefined,
        note: note.trim() || undefined,
      });
      toast.success(st('xac_nhan_doi_soat_thanh_cong_message'));
      onConfirmed();
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_xac_nhan_doi_soat_message'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!target) return null;

  return (
    <Modal isOpen={isOpen} toggle={handleToggle} centered>
      <ModalHeader className="bg-light p-3" toggle={handleToggle}>
        {st('xac_nhan_doi_soat_title')} {getAgentName(agentMap, target.agentId)}
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <div className="mb-3 text-muted">
            {st('da_giao_text')} <strong>{target.deliveredQuantity.toLocaleString('vi-VN')}</strong> ·
            {' '}{st('thu_hoi_text')} <strong>{target.recalledQuantity.toLocaleString('vi-VN')}</strong> ·
            {' '}{st('da_ban_suy_dien_text')} <strong>{target.soldQuantity.toLocaleString('vi-VN')}</strong>
          </div>
          <FormGroup>
            <Label>{st('so_chenh_lech_phat_hien_label')}</Label>
            <NumberInput value={discrepancyQuantity} onChange={setDiscrepancyQuantity} />
          </FormGroup>
          <FormGroup>
            <Label>{st('ly_do_chenh_lech_label')} {discrepancyQuantity !== 0 && <span className="text-danger">*</span>}</Label>
            <Input
              type="textarea"
              rows={2}
              value={discrepancyReason}
              onChange={(e) => setDiscrepancyReason(e.target.value)}
              placeholder={st('bat_buoc_nhap_neu_so_chenh_placeholder')}
            />
          </FormGroup>
          <FormGroup>
            <Label>{st('ghi_chu_label')}</Label>
            <Input type="textarea" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </FormGroup>
        </ModalBody>
        <div className="modal-footer">
          <div className="hstack gap-2 justify-content-end">
            <Button color="light" onClick={handleToggle} disabled={submitting}>{st('huy_column')}</Button>
            <Button color="success" type="submit" disabled={submitting}>
              {submitting && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
              {st('xac_nhan_button')}
            </Button>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default ConfirmReconciliationModal;
